import { Injectable } from '@angular/core'

import { Subject, filter, fromEvent, merge, take, takeUntil } from 'rxjs'

import { DragInitParams } from './drag.models'

const MOUSE_EVENT_LEFT_CLICK_CODE = 1

@Injectable({
  providedIn: 'root',
})
export class DragService {
  init(
    element: HTMLElement,
    { onDragEnd, onDragMove }: Record<string, (params: DragInitParams) => void>
  ): Subject<any> {
    const dragObs$ = new Subject<void>()

    const dragStart$ = fromEvent<MouseEvent>(element, 'mousedown').pipe(
      filter((event) => event.buttons === MOUSE_EVENT_LEFT_CLICK_CODE)
    )
    const dragEnd$ = fromEvent<MouseEvent>(element.getRootNode(), 'mouseup')
    const dragMove$ = fromEvent<MouseEvent>(element.getRootNode(), 'mousemove')

    let deltaX = 0
    let deltaY = 0

    dragStart$.pipe(takeUntil(dragObs$)).subscribe({
      next: (startEvent: MouseEvent) => {
        const { x: elementX, y: elementY } = (
          startEvent.currentTarget as HTMLElement
        ).getBoundingClientRect()
        const { clientX: startX, clientY: startY } = startEvent

        deltaX = elementX - startX
        deltaY = elementY - startY

        dragMove$.pipe(takeUntil(merge(dragEnd$, dragObs$))).subscribe({
          next: (moveEvent: MouseEvent) => {
            const { clientX: targetX, clientY: targetY } = moveEvent

            onDragMove({
              from: { x: elementX, y: elementY },
              to: { x: targetX + deltaX, y: targetY + deltaY },
              delta: { x: deltaX, y: deltaY },
              deltaAbs: { x: targetX - startX, y: targetY - startY },
            })
          },
        })

        dragEnd$.pipe(take(1), takeUntil(dragObs$)).subscribe({
          next: (endEvent: MouseEvent) => {
            endEvent.stopImmediatePropagation()
            endEvent.preventDefault()

            const { clientX: endX, clientY: endY } = endEvent

            onDragEnd({
              from: { x: elementX, y: elementY },
              to: { x: endX + deltaX, y: endY + deltaY },
              delta: { x: endX - startX, y: endY - startY },
            })
          },
        })
      },
    })

    return dragObs$
  }
}
